/**
 * compositeOutputs.js
 *
 * Stage 3 — defines Composite Analytical Outputs: analytical results
 * composed from MULTIPLE existing Adapters/Owners, as distinct from the
 * 4 single-source Datasets in analyticalQuery.js's DATASETS registry.
 *
 * S-Curve is the only Composite Output in v1. It is NOT a 5th dataset —
 * it has no single Owner and no AnalyticalQuery shape of its own (no
 * measures/dimension selection the way cost_performance does). It
 * combines two ALREADY-EXISTING, unmodified Adapters:
 *   - plannedBudgetAdapter.js  (Planned Budget, monthly calendar grain)
 *   - timeSeriesSSOTAdapter.js (Actual + EAC, forecast snapshot grain)
 * via sCurveSSOTAdapter.js (Phase 5D, built and reconciliation-tested
 * previously — zero changes made to it in this Stage).
 *
 * Per the approved plan: "Keep S-Curve as a visualization/composite
 * capability, not a separate subsystem" — this file is the smallest
 * possible seam that lets the Report Builder offer S-Curve as a
 * selectable output type, without touching cost_performance/
 * historical_comparison/time_series/monthly_actual_cost's contracts at
 * all.
 */

import { buildSCurveResult } from './sCurveSSOTAdapter'
import { resolveAllAvailableSnapshots } from './temporalResolver'

export const COMPOSITE_OUTPUTS = {
  s_curve: {
    label: 'S-Curve',
    description: 'Planned Budget vs Actual Cumulative vs EAC Forecast, over time.',
    fixedSeries: ['planned_budget', 'actual', 'eac'],
    temporalInput: { mode: 'all_available' },
  },
}

/**
 * Reshapes sCurveSSOTAdapter.js's SCurveResult (series[i] = {time,
 * planned_budget, actual, eac, hasSnapshot, sourcesPresent} — values
 * directly on the point) into the SAME canonical TimeSeriesResult shape
 * Bar/Table/Line/Combo already consume (series[i] = {time, hasSnapshot,
 * rows: [{key, label, values}]}). Discovered during Stage 9: these two
 * shapes are NOT identical, despite both being "time series" results —
 * sCurveSSOTAdapter.js predates the Visual Engine's canonical contract
 * and was built/reconciled (Phase 5D) against a bespoke SCurveChart
 * sub-component inside Line.jsx's `mode: 's_curve'`, not against
 * Bar/Table/Combo's generic {key,label,values} row contract.
 *
 * This function performs NO financial calculation — it is a pure
 * relabeling of already-computed values into a different object shape,
 * same posture as every other Adapter's reshape-only responsibility.
 * planned_budget/actual/eac stay exactly as sCurveSSOTAdapter.js
 * computed them, including their null semantics (a null value here
 * means "no source for this point", never coerced to 0).
 */
function reshapeSCurveToCanonical(sCurveResult) {
  return {
    grain: 'project',
    series: sCurveResult.series.map(point => ({
      time: point.time,
      hasSnapshot: point.hasSnapshot,
      rows: [{
        key: 's_curve',
        label: 'Project',
        values: {
          planned_budget: point.planned_budget,
          actual: point.actual,
          eac: point.eac,
        },
      }],
    })),
  }
}

/**
 * Executes the S-Curve composite output. Same resolution pattern as
 * queryDispatcher.js's time_series branch (resolveAllAvailableSnapshots()
 * then call the existing Adapter) — no new temporal syntax invented.
 * Returns the CANONICAL shape (via reshapeSCurveToCanonical), not
 * sCurveSSOTAdapter.js's raw SCurveResult — so the Report Builder's
 * generic VisualComponentRenderer (Bar/Table/Line/Combo) works
 * identically for S-Curve as for every Dataset, with zero Visual
 * component changes.
 */
export async function executeCompositeOutput(projectId, compositeKey) {
  if (compositeKey !== 's_curve') {
    throw new Error(`executeCompositeOutput: unknown composite output "${compositeKey}"`)
  }
  const forecastDates = await resolveAllAvailableSnapshots(projectId)
  const rawResult = await buildSCurveResult(projectId, forecastDates)
  return reshapeSCurveToCanonical(rawResult)
}
